import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, FormHelperText, Divider, Fab } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IAppointment, AppointmentUpdateDateTimeRequest } from "@ontime/types";
import AppointmentService from '../../services/AppointmentService';
import dayjs from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';
import { green } from '@mui/material/colors';
import { useSelector } from 'react-redux';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';
import { AppointmentStatus } from "@ontime/types";

const appointmentSchema = z.object({
    appointmentId: z.string(),
    version: z.number(),
    serviceProviderId: z.string(),
    startDateTime: z.string(),
    endDateTime: z.string().optional(),
});

interface AppointmentFormProps {
    closeForm: (input?: IAppointment) => void;
    updateExistData: (input: IAppointment) => void;
    appointment: IAppointment;
    AllAppointment: IAppointment[] | [];
    disabled: boolean;
}

const AppointmentDateTimeForm: React.FC<AppointmentFormProps> = ({ closeForm, appointment, updateExistData, AllAppointment, disabled }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [minutes, setMinutes] = useState(15);

    const selectedOrganization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<AppointmentUpdateDateTimeRequest>({
        resolver: zodResolver(appointmentSchema),
        defaultValues: {
            appointmentId: '',
            serviceProviderId: '',
            startDateTime: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
            endDateTime: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
            version: 0,
        },
    });

    const handleDurationClick = (minutesToAdd: number) => {
        const start = dayjs(watch('startDateTime'));
        const end = start.add(minutesToAdd, 'minute');
        setValue('endDateTime', end.format('YYYY-MM-DDTHH:mm:ss.SSS'));
        setMinutes(minutesToAdd);
    };

    useEffect(() => {
        if (appointment) {

            const endDateTime = appointment.endDateTime ? dayjs(appointment.endDateTime) : dayjs(appointment.startDateTime).add(15, 'minute');

            reset({
                appointmentId: appointment.appointmentId,
                serviceProviderId: appointment.serviceProviderId,
                startDateTime: appointment.startDateTime,
                endDateTime: appointment.endDateTime ? appointment.endDateTime : endDateTime.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                version: appointment.version
            });

            setMinutes(endDateTime.diff(dayjs(appointment.startDateTime), 'minute'))

        }
    }, [appointment, reset]);

    const [timeLoading, setTimeLoading] = useState(false);

    const handleSave = async (input: AppointmentUpdateDateTimeRequest) => {
        setTimeLoading(true);

        const newStart = dayjs(input.startDateTime);
        const newEnd = dayjs(input.endDateTime);

        const isOverlapping = AllAppointment.filter((item) => item.status !== AppointmentStatus.Canceled).some(app => {
            if (appointment?.appointmentId && app.appointmentId === appointment.appointmentId) return false;

            const existingStart = dayjs(app.startDateTime);
            const existingEnd = dayjs(app.endDateTime);

            return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
        });

        if (isOverlapping) {
            enqueueSnackbar('The selected time overlaps with an existing appointment.', { variant: 'error' });
            setTimeLoading(false);
            return;
        }

        try {

            const response = await AppointmentService.UpdateDateTime(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, input);
            // if (response.response.success) {
            //     enqueueSnackbar('Saved successfully!', { variant: 'success' });
            updateExistData({ ...appointment, endDateTime: response.endDateTime, startDateTime: response.startDateTime, version: response.version });
            // } else {
            //     enqueueSnackbar(response.response.error?.message, { variant: 'error' });
            // }

            // } catch (error) {
            //     enqueueSnackbar('Error occurred while saving. Please try again later.', { variant: 'error' });
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setTimeLoading(false);
        }
    };


    const startDateTime = watch('startDateTime');
    const endDateTime = watch('endDateTime');

    const isDirty =
        startDateTime !== appointment.startDateTime ||
        endDateTime !== appointment.endDateTime;

    return (
        <form onSubmit={handleSubmit(handleSave)} noValidate>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Controller
                    name="startDateTime"
                    control={control}
                    render={({ field }) => (
                        <TimePicker
                            label="Start Time"
                            value={dayjs(field.value)}
                            onChange={(newTime) => {
                                const currentDate = dayjs(watch('startDateTime'));
                                const updatedDateTime = currentDate
                                    .hour(newTime?.hour() || 0)
                                    .minute(newTime?.minute() || 0)
                                    .second(0)
                                    .millisecond(0)
                                    .toISOString();

                                field.onChange(updatedDateTime);

                                const newEnd = dayjs(updatedDateTime).add(minutes, 'minute').toISOString();
                                setValue('endDateTime', newEnd);
                            }}
                            slotProps={{ textField: { fullWidth: true } }}
                            minutesStep={15}
                            disabled={timeLoading || disabled}
                        />
                    )}
                />
                <FormControl fullWidth error={!!errors.endDateTime} disabled={timeLoading || disabled} >
                    <InputLabel>Duration</InputLabel>
                    <Select
                        label="Duration"
                        value={minutes}
                        onChange={(e) => handleDurationClick(Number(e.target.value))}
                    >
                        <MenuItem value='15'> 15M</MenuItem>
                        <MenuItem value='30'> 30M</MenuItem>
                        <MenuItem value='45'> 45M</MenuItem>
                        <MenuItem value='60'> 1H</MenuItem>
                        <Divider />
                        <MenuItem value='75'> 1:15</MenuItem>
                        <MenuItem value='90'> 1:30</MenuItem>
                        <MenuItem value='105'> 1:45</MenuItem>
                        <MenuItem value='120'> 2H</MenuItem>
                        <Divider />
                        <MenuItem value='135'> 2:15</MenuItem>
                        <MenuItem value='150'> 2:30</MenuItem>
                        <MenuItem value='165'> 2:45</MenuItem>
                        <MenuItem value='180'> 3H</MenuItem>
                        <Divider />
                        <MenuItem value='240'> 4H</MenuItem>
                        <MenuItem value='300'> 5H</MenuItem>
                        <MenuItem value='360'> 6H</MenuItem>
                        <MenuItem value='420'> 7H</MenuItem>
                        <MenuItem value='480'> 8H</MenuItem>
                        <MenuItem value='540'> 9H</MenuItem>
                        <MenuItem value='600'> 10H</MenuItem>
                    </Select>
                    <FormHelperText>{errors.endDateTime?.message}</FormHelperText>
                </FormControl>
                <Box sx={{ position: 'relative' }}>
                    <Fab disabled={timeLoading || !isDirty || disabled} size='small' aria-label="save" color="primary" type="submit">
                        {!isDirty ? <CheckIcon /> : <SaveIcon />}
                    </Fab>
                    {timeLoading && (
                        <CircularProgress
                            size={48}
                            sx={{
                                color: green[500],
                                position: 'absolute',
                                top: -4,
                                left: -4,
                                zIndex: 1,
                            }}
                        />
                    )}
                </Box>
            </Box>
        </form>
    );
};

export default AppointmentDateTimeForm;